import { useQueryClient } from '@tanstack/vue-query'
import { storeToRefs } from 'pinia'
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAppsQuery } from '@dashboard/modules/integration/composables/useApps'
import type { AppListing, AppSyncState } from '@dashboard/modules/integration/composables/useApps'
import { ROUTES } from '@dashboard/router/routeNames'
import { createRealtimeQueryReconciler } from '@dashboard/services/realtimeQueryReconciler'
import { observeResource } from '@dashboard/services/realtimeResourceObserver'
import { invalidateRevenueQueries } from '@dashboard/services/revenueQueryKeys'
import { useConfigStore } from '@dashboard/stores/configStore'
import { useNotificationStore } from '@dashboard/stores/notificationStore'
import { useTeamStore } from '@dashboard/stores/teamStore'

const IMPORT_DONE_LIFE_MS = 12_000

/**
 * Only payment sources (Stripe's `revenue` listing) import payments. Other syncing apps, such as Meta Ads, show their
 * own import on their page and never get a revenue notice.
 */
const paysIn = (app: AppListing) => app.listing.category === 'revenue'

/**
 * Payment imports run in the background, so whoever connected a payment app keeps working and hears when its first
 * window is in (or stopped on a rejected key), wherever they are in the app. Realtime `apps.sync.{key}` events refresh
 * the shared apps directory while an import runs; the notice comes from the observed transition out of Importing, so a
 * missed event caught up on resubscribe, or a page's own poll, still notifies exactly once.
 */
export function usePaymentImportNotices() {
  const { currentTeamPid } = storeToRefs(useConfigStore())
  const { isDemo } = storeToRefs(useTeamStore())
  const queryClient = useQueryClient()
  const notifications = useNotificationStore()
  const router = useRouter()
  const appsQuery = useAppsQuery(() => !isDemo.value, { realtime: true })
  const refreshApps = createRealtimeQueryReconciler(
    () => appsQuery.isFetching.value,
    () => appsQuery.refetch()
  )

  let seen: { teamPid: number; states: Map<string, AppSyncState> } | null = null
  watch(appsQuery.data, (apps) => {
    const teamPid = currentTeamPid.value
    if (!apps || typeof teamPid !== 'number') {
      return
    }
    const previous = seen?.teamPid === teamPid ? seen.states : null
    const states = new Map<string, AppSyncState>()
    for (const app of apps.filter(paysIn)) {
      const state = app.connection?.connected ? app.connection.sync?.state : undefined
      if (!state) {
        continue
      }
      states.set(app.key, state)
      if (previous?.get(app.key) === 'importing' && state !== 'importing') {
        announce(teamPid, app, state)
      }
    }
    seen = { teamPid, states }
  })

  const importing = computed(() =>
    (appsQuery.data.value ?? [])
      .filter((app) => paysIn(app) && app.connection?.connected && app.connection.sync?.state === 'importing')
      .map((app) => app.key)
  )
  watch(
    [currentTeamPid, () => importing.value.join(',')],
    ([teamPid], _previous, onCleanup) => {
      if (typeof teamPid !== 'number' || importing.value.length === 0) {
        return
      }
      const controller = new AbortController()
      for (const key of importing.value) {
        observeResource(
          String(teamPid),
          `apps.sync.${key}`,
          () => {
            void refreshApps()
          },
          controller.signal
        )
      }
      onCleanup(() => controller.abort())
    },
    { immediate: true }
  )

  function announce(teamPid: number, app: AppListing, state: AppSyncState) {
    void invalidateRevenueQueries(queryClient, teamPid)
    const name = app.listing.name
    if (state === 'reconnect') {
      notifications.add({
        type: 'error',
        sticky: true,
        message: `${name} import stopped`,
        detail: app.connection?.sync?.message ?? undefined,
        action: {
          label: 'Reconnect',
          onClick: () => {
            void router.push({ name: ROUTES.INTEGRATIONS, params: { teamPid }, query: { tab: 'apps' } })
          }
        }
      })
      return
    }
    notifications.add({
      type: 'success',
      life: IMPORT_DONE_LIFE_MS,
      message: `${name} payments are in`,
      detail: 'Revenue from the last 90 days is ready.',
      action: {
        label: 'View revenue',
        onClick: () => {
          void router.push({ name: ROUTES.SEGMENTGROUPS, params: { teamPid }, query: { metric: 'revenue' } })
        }
      }
    })
  }
}
